import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { UserContextService } from '../../../common/user-management/auth-management/authentication/services/user-context.service';
import { WalletStateService } from '../../../financial-management/wallets';
import { RiyalSymbolComponent } from "../../core/ui/riyal-symbol/riyal-symbol.component";

interface PaymentMethodLookup {
  id: string;
  englishName?: string;
  arabicName?: string;
  name?: string;
}

interface PaymentMethodCardItem {
  id: string;
  methodIds: string[];
  label: string;
  logoIds: string[];
  isWallet?: boolean;
}

@Component({
  selector: 'app-payment-methods-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, RiyalSymbolComponent],
  templateUrl: './payment-methods-card.component.html',
  styleUrl: './payment-methods-card.component.scss'
})
export class PaymentMethodsCardComponent {
  private walletState = inject(WalletStateService);
  private readonly walletMethodId = 'WALLET';
  @Input() paymentMethods: PaymentMethodLookup[] = [];
  @Input() selectedMethodId = '';
  @Input() titleKey = 'SUBSCRIPTION_DETAILS.PAYMENT_METHOD';
  @Input() showWalletOption = true;
  @Input() walletOptionDisabled = false;

  @Output() methodSelected = new EventEmitter<string>();

  private readonly groupedCardMethodIds = ['VISA', 'MASTERCARD', 'AMEX', 'MADA'];
  private readonly methodOrder = ['APPLEPAY'];
  wallet = this.walletState.wallet;

  get hasApplePay(): boolean {
    return this.paymentMethods.some((m) => this.normalizeMethodId(m.id) === 'APPLEPAY');
  }

  get methodItems(): PaymentMethodCardItem[] {
    const groupedMethods = this.paymentMethods.filter((method) =>
      this.groupedCardMethodIds.includes(this.normalizeMethodId(method.id))
    );
    const items: PaymentMethodCardItem[] = [];

    if (groupedMethods.length > 0) {
      items.push({
        id: 'CARD_SCHEMES',
        methodIds: groupedMethods.map((method) => method.id),
        label: 'SHARED.PAYMENT_CARD_NETWORKS_LABEL',
        logoIds: this.getCardSchemeLogos(groupedMethods)
      });
    }

    const nonGrouped = this.paymentMethods.filter(
      (method) => !this.groupedCardMethodIds.includes(this.normalizeMethodId(method.id))
    );
    const sortedNonGrouped = [...nonGrouped].sort(
      (a, b) => this.getMethodSortWeight(this.normalizeMethodId(a.id)) - this.getMethodSortWeight(this.normalizeMethodId(b.id))
    );

    sortedNonGrouped.forEach((method) => {
      items.push({
        id: method.id,
        methodIds: [method.id],
        label: this.getMethodLabel(method),
        logoIds: [method.id]
      });
    });

    if (this.showWalletOption && UserContextService.isAuthenticated()) {
      items.push({
        id: this.walletMethodId,
        methodIds: [this.walletMethodId],
        label: 'WALLET.USE_WALLET_BALANCE',
        logoIds: [this.walletMethodId],
        isWallet: true
      });
    }

    return items;
  }

  isSelected(item: PaymentMethodCardItem): boolean {
    return item.methodIds.includes(this.selectedMethodId);
  }

  onSelect(item: PaymentMethodCardItem): void {
    if (item.isWallet && this.walletOptionDisabled) {
      return;
    }

    if (item.methodIds.length === 1) {
      this.methodSelected.emit(item.methodIds[0]);
      return;
    }

    if (item.methodIds.includes(this.selectedMethodId)) {
      this.methodSelected.emit(this.selectedMethodId);
      return;
    }

    const preferredOrder = ['VISA', 'MASTERCARD', 'AMEX', 'MADA'];
    const selectedFallback =
      preferredOrder.find((methodId) =>
        item.methodIds.some((id) => this.normalizeMethodId(id) === methodId)
      ) || item.methodIds[0] || '';

    if (selectedFallback) {
      this.methodSelected.emit(selectedFallback);
    }
  }

  getIconPath(logoId: string): string {
    const normalizedId = this.normalizeMethodId(logoId);
    if (normalizedId === this.walletMethodId) {
      return 'assets/images/icons/wallet-icon.svg';
    }
    return `assets/images/payment-methods/${normalizedId}.svg`;
  }

  isLogoAvailable(logoId: string): boolean {
    return ['VISA', 'MASTERCARD', 'MADA', 'APPLEPAY', 'AMEX', this.walletMethodId].includes(this.normalizeMethodId(logoId));
  }

  getWalletBalanceAmount(): number | null {
    return this.wallet()?.totalBalance?.amount ?? null;
  }

  private getMethodLabel(method: PaymentMethodLookup): string {
    const normalizedId = this.normalizeMethodId(method.id);
    if (normalizedId === 'APPLEPAY') {
      return 'Apple Pay';
    }
    return method.englishName || method.name || method.id;
  }

  private getCardSchemeLogos(methods: PaymentMethodLookup[]): string[] {
    const preferredOrder = ['VISA', 'MASTERCARD', 'AMEX', 'MADA'];
    return preferredOrder.filter((methodId) =>
      methods.some((method) => this.normalizeMethodId(method.id) === methodId)
    );
  }

  private getMethodSortWeight(methodId: string): number {
    const explicitIndex = this.methodOrder.indexOf(methodId);
    if (explicitIndex > -1) {
      return explicitIndex;
    }
    return this.methodOrder.length + 1;
  }

  private normalizeMethodId(methodId: string): string {
    const upperMethodId = (methodId || '').toUpperCase();
    if (upperMethodId === 'MASTER') {
      return 'MASTERCARD';
    }
    if (upperMethodId === 'AMERICANEXPRESS') {
      return 'AMEX';
    }
    return upperMethodId;
  }
}
